// Contacts API service
import { baseClient } from './baseClient';
import type {
  Contact,
  ContactCreate,
  ContactReadWithAccount,
  ContactReadWithInsights,
  ContactFilters
} from '../types/api';

export class ContactsApi {
  private static instance: ContactsApi;

  private constructor() { }

  public static getInstance(): ContactsApi {
    if (!ContactsApi.instance) {
      ContactsApi.instance = new ContactsApi();
    }
    return ContactsApi.instance;
  }

  /**
   * Get all contacts with optional filtering
   */
  async getContacts(filters?: ContactFilters): Promise<ContactReadWithAccount[]> {
    const params = new URLSearchParams();

    if (filters?.q) params.append('q', filters.q);
    if (filters?.skip) params.append('skip', filters.skip.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.account_id) params.append('account_id', filters.account_id);

    const queryString = params.toString();
    const url = queryString ? `/contacts?${queryString}` : '/contacts';

    return baseClient.get<ContactReadWithAccount[]>(url);
  }

  /**
   * Get a single contact by ID with account details
   */
  async getContact(id: string): Promise<ContactReadWithAccount> {
    return baseClient.get<ContactReadWithAccount>(`/contacts/${id}`);
  }

  /**
   * Get a single contact by ID with insights
   */
  async getContactWithInsights(id: string): Promise<ContactReadWithInsights> {
    return baseClient.get<ContactReadWithInsights>(`/contacts/${id}`);
  }

  /**
   * Create a new contact
   */
  async createContact(contact: ContactCreate): Promise<Contact> {
    return baseClient.post<Contact>('/contacts', contact);
  }

  /**
   * Update an existing contact
   */
  async updateContact(id: string, contact: ContactCreate): Promise<Contact> {
    return baseClient.put<Contact>(`/contacts/${id}`, contact);
  }

  /**
   * Delete a contact
   */
  async deleteContact(id: string): Promise<void> {
    return baseClient.delete<void>(`/contacts/${id}`);
  }

  /**
   * Search contacts by name or email
   */
  async searchContacts(query: string): Promise<ContactReadWithAccount[]> {
    return this.getContacts({ q: query });
  }

  /**
   * Get contacts by account
   */
  async getContactsByAccount(accountId: string): Promise<ContactReadWithAccount[]> {
    return this.getContacts({ account_id: accountId });
  }

  /**
   * Get contact by email
   */
  async getContactByEmail(email: string): Promise<Contact | null> {
    try {
      const contacts = await this.searchContacts(email);
      return contacts.find(contact => contact.email === email) || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if contact email exists
   */
  async checkEmailExists(email: string): Promise<boolean> {
    const contact = await this.getContactByEmail(email);
    return contact !== null;
  }

  /**
   * Get contact statistics
   */
  async getContactStats(contactId: string): Promise<any> {
    const contact = await this.getContactWithInsights(contactId);

    return {
      totalInsights: contact.insights.length,
      insightsByAgent: contact.insights.reduce((acc, insight) => {
        acc[insight.generated_by_agent] = (acc[insight.generated_by_agent] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      latestInsight: contact.insights.length > 0
        ? contact.insights.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
        : null,
    };
  }

  /**
   * Bulk create contacts
   */
  async bulkCreateContacts(contacts: ContactCreate[]): Promise<Contact[]> {
    const promises = contacts.map(contact => this.createContact(contact));
    return Promise.all(promises);
  }

  /**
   * Get contacts with recent activity
   */
  async getContactsWithRecentActivity(days: number = 30): Promise<ContactReadWithAccount[]> {
    const contacts = await this.getContacts();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    // This would need to be implemented on the backend to be efficient
    // For now, we'll return all contacts and filter on frontend
    return contacts;
  }
}

// Export singleton instance
export const contactsApi = ContactsApi.getInstance(); 